import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  NgbModal,
  NgbTypeaheadSelectItemEvent,
} from '@ng-bootstrap/ng-bootstrap';
import { forkJoin, Observable, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  switchMap, takeUntil,
  tap,
} from 'rxjs/operators';
import { tourHolidayTypeList } from 'src/app/modules/booking-flow/holiday/holiday-search-page/helpers/holiday-types';
import { BookingService } from '../../../shared/services/booking.service';
import { HolidayPrefetchService } from '../../../shared/services/holiday-prefetch.service';
import { MappingService } from '../../../shared/services/mapping.service';
import { MobileService } from '../../../shared/services/mobile.service';
import { ModalStatusService } from '../../../shared/services/modal-status.service';
import { IDestinationBox } from '../../interfaces/idestination-box';
import { IDestinationBoxElement } from '../../interfaces/idestination-box-element';
import { IEngineParamChange } from '../../interfaces/iengine-param-change';
import { EngineTabTypes } from '../../models/engine-tab-types.enum';
import { HolidayEngineService } from '../../services/holiday-engine.service';
import { ActivatedRoute } from "@angular/router";
import {
  createPrefetchParams
} from "../../../booking-flow/tour/tour-search-page/utils/payload-tour-search-generator.utils";

@Component({
  selector: 'app-engine-tour-modal-destinations',
  templateUrl: './engine-tour-modal-destinations.component.html',
  styleUrls: ['./engine-tour-modal-destinations.component.scss'],
})
export class EngineTourModalDestinationsComponent implements OnInit, OnChanges, OnDestroy {
  prefetchDestinations$: Observable<number[]>;
  destinationBox$: Observable<IDestinationBox[]>;
  destinationSelected: any;
  @Output() selectedDestination = new EventEmitter<IEngineParamChange<IDestinationBoxElement>>();
  @Input() defaultDestination: number;
  @Input() onlyCustomHoliday: boolean;
  @Input() onlyNetQuotes: boolean;
  @Input() engineTabType: EngineTabTypes;

  private _destroy$ = new Subject<boolean>();

  constructor(
    private _modalService: NgbModal,
    private _route: ActivatedRoute,
    private _modalStatusService: ModalStatusService,
    private _holidayPrefetchService: HolidayPrefetchService,
    private _holidayEngineService: HolidayEngineService,
    private _mappingService: MappingService,
    private _bookingService: BookingService,
    private _mobileService: MobileService
  ) { }

  selectedDestinationInner: IDestinationBoxElement;

  loadingStatus: boolean;
  loadingMessage: string;
  isMobile$: Observable<boolean>;

  typeaheadLoading = false;

  ngOnInit(): void {
    this.destinationSelected = this._route.snapshot.paramMap.get('destinationId');
    if (this.destinationSelected) {
      this._mappingService.getDestinationByID(this.destinationSelected)
        .pipe(takeUntil(this._destroy$))
        .subscribe((res) => {
          this.selectedDestinationInner = {
            id: res.Id,
            name: res.Name,
            isTour: true
          };
          this.selectedDestination.emit({ value: this.selectedDestinationInner, resetParams: false });
        })
    }
    const prefetchParams = createPrefetchParams(this.onlyCustomHoliday, this.onlyNetQuotes, this._bookingService.getAgesArray());
    if (this.engineTabType === EngineTabTypes.Tour) {
      prefetchParams.holidayTypeList = tourHolidayTypeList;
    }
    this.prefetchDestinations$ = this._holidayPrefetchService.prefetchDestinations(prefetchParams);
    this.loadingStatus = true;
    this.loadingMessage = 'Stiamo preparando le destinazioni...';
    this.prefetchDestinations$.subscribe((e) => {
      this.destinationBox$ = this._holidayEngineService
        .getFormattedDestionationBox(e)
        .pipe(
          finalize(() => setTimeout(() => (this.loadingStatus = false), 0))
        );
    });
    this.isMobile$ = this._mobileService.isMobile();
  }

  open(content: any): void {
    if (!this._modalService.hasOpenModals()) {
      this._modalStatusService.hasOpenModals.next(true);
      this._modalService
        .open(content, {
          ariaLabelledBy: 'modal-engine',
          size: 'xl',
        })
        .result.finally(() =>
          this._modalStatusService.hasOpenModals.next(false)
        );
    }
  }

  onSelectedDestination(destination: IDestinationBoxElement): void {
    this.selectDestination(destination, false, true);
  }

  selectDestination(
    destination: IDestinationBoxElement,
    resetParams: boolean = true,
    loadNextModal: boolean = false
  ): void {
    this.selectedDestinationInner = destination;
    this.selectedDestination.emit({ value: destination, resetParams, isTour: this.engineTabType === 'tour' });
    this._modalService.dismissAll();
    if (!loadNextModal) return;
    setTimeout(() => {
      const nextModalInput = document.getElementById('inputAirportHoliday');
      if (nextModalInput) nextModalInput.click();
    }, 100);
  }

  destinationFormatter(result: IDestinationBoxElement): string {
    return result.name;
  }

  destinationChangeCB($event: NgbTypeaheadSelectItemEvent): void {
    this.selectDestination($event.item);
  }

  destinationClearCB(): void {
    this.selectedDestination.emit({ value: undefined, resetParams: true });
    this.selectedDestinationInner = undefined;
  }

  destinationSearch = (text$: Observable<string>) =>
    text$.pipe(
      tap(() => (this.typeaheadLoading = true)),
      debounceTime(500),
      distinctUntilChanged(),
      switchMap((text) =>
        forkJoin([
          this.prefetchDestinations$,
          this._mappingService.getHolidayMappings(),
        ]).pipe(
          map((e) => {
            return e[0]
              .map((i) => e[1].destinations.objects[i])
              .filter((x) =>
                x ? x.Name.toLowerCase().includes(text.toLowerCase()) : false
              )
              .map((i) => ({ name: i.Name, id: i.Id }));
          }),
          tap(() => (this.typeaheadLoading = false))
        )
      )
    );

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.defaultDestination && this.defaultDestination) {
      this._mappingService
        .getDestinationByID(this.defaultDestination)
        .pipe(takeUntil(this._destroy$))
        .subscribe((e) => {
          const destination: IDestinationBoxElement = {
            id: e.Id,
            name: e.Name,
          };
          this.selectDestination(destination, false, false);
        });
    }
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
